import type { SessionMessageEntry, ToolCallRunResponse } from '@erp/rpc';

// ---------------------------------------------------------------------------
// Pure screenshot-gallery logic (no React) — shared by AgentSessionViewer and
// unit-tested directly. Turns persisted tool-call rows into the ordered gallery
// items the viewer renders, and links each frame to its thread message.
// ---------------------------------------------------------------------------

/**
 * Shared screenshot item type — normalises base64 (live) & signed-URL sources.
 */
export interface ScreenshotItem {
  id: string;
  src: string;
  label: string;
  coordinate?: [number, number];
  actionType?: string;
  actionText?: string;
  /** A2A messageId / tool-call id of the thread entry this frame belongs to. */
  messageId?: string;
}

/** A cited thread message or a thumbnail the user selected directly. */
export type ScreenshotSelection = {
  kind: 'message' | 'item';
  id: string;
};

export function screenshotMatchesSelection(
  item: ScreenshotItem,
  selection: ScreenshotSelection | null | undefined,
): boolean {
  if (!selection) return false;
  return selection.kind === 'message'
    ? item.messageId === selection.id
    : item.id === selection.id;
}

/** The tool-call fields the gallery reads. */
type ScreenshotToolCall = Pick<
  ToolCallRunResponse,
  'id' | 'toolType' | 'inputData' | 'outputData' | 'screenshotSignedUrl'
>;

export function extractActionData(
  inputData: Record<string, unknown>,
  outputData?: Record<string, unknown> | null,
): {
  coordinate?: [number, number];
  action?: string;
  text?: string;
  viewportOffset?: [number, number];
} {
  const action =
    typeof inputData.action === 'string' ? inputData.action : undefined;
  const text = typeof inputData.text === 'string' ? inputData.text : undefined;
  const rawCoord = inputData.coordinate;
  const coordinate =
    Array.isArray(rawCoord) &&
    rawCoord.length === 2 &&
    typeof rawCoord[0] === 'number' &&
    typeof rawCoord[1] === 'number'
      ? (rawCoord as [number, number])
      : undefined;
  const rawOffset = outputData?.viewportOffset;
  const viewportOffset =
    Array.isArray(rawOffset) &&
    rawOffset.length === 2 &&
    typeof rawOffset[0] === 'number' &&
    typeof rawOffset[1] === 'number'
      ? (rawOffset as [number, number])
      : undefined;
  return { coordinate, action, text, viewportOffset };
}

/** Human label for a tool-call frame from its `agent.<surface>.action.<name>` type. */
export function getToolCallLabel(toolType: string | null | undefined): string {
  if (!toolType) return 'Screenshot';
  // Operator (web_browser) and remote-desktop actions share the
  // `agent.<surface>.action.<name>` shape — title-case the action name.
  const match = toolType.match(
    /^agent\.(?:remote_desktop|web_browser)\.action\.(.+)$/,
  );
  if (match) {
    return match[1]
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return 'Screenshot';
}

/**
 * Build the completed-run gallery items from screenshot-bearing tool calls.
 * Two shapes, chosen by whether an initial (pre-first-action) frame exists:
 *
 * - WITH an initial frame (remote-desktop path): each stored screenshot is the
 *   frame AFTER its action, but the agent picked its click coordinate from the
 *   frame BEFORE. So action k is shown on its before-frame (the initial frame
 *   for the first action, the prior action's frame otherwise) — the dot lands
 *   where it clicked — and a trailing item shows the final post-action frame.
 *
 * - WITHOUT an initial frame (the A2A operator persists only each step's
 *   resulting frame, no initial): map each step 1:1 to its OWN frame, so
 *   clicking a step shows what that step produced. This removes the off-by-one
 *   the before-frame shift caused for the operator — with no initial frame the
 *   first step was dropped (`continue`) and every message↔screenshot link slid
 *   by one. Click coordinates are omitted (the dot belongs on the before-frame,
 *   which isn't shown here); scroll/type labels still render.
 *
 * Each action item carries the tool-call id as its messageId so the gallery ↔
 * persisted-thread linking stays structural. An initial frame with no action
 * screenshots stands alone, without an action overlay or message link.
 */
export function buildScreenshotItems({
  toolCalls,
  initialScreenshotUrl,
  finalLabel,
  initialLabel = finalLabel,
}: {
  toolCalls: ScreenshotToolCall[];
  initialScreenshotUrl: string | null;
  finalLabel: string;
  initialLabel?: string;
}): ScreenshotItem[] {
  const tcs = toolCalls.filter(
    (tc): tc is ScreenshotToolCall & { screenshotSignedUrl: string } =>
      Boolean(tc.screenshotSignedUrl),
  );
  if (tcs.length === 0) {
    return initialScreenshotUrl
      ? [{ id: 'initial', src: initialScreenshotUrl, label: initialLabel }]
      : [];
  }

  if (!initialScreenshotUrl) {
    return tcs.map((tc) => {
      const { action, text } = extractActionData(
        tc.inputData,
        tc.outputData as Record<string, unknown> | null,
      );
      return {
        id: tc.id,
        src: tc.screenshotSignedUrl,
        label: getToolCallLabel(tc.toolType),
        actionType: action,
        actionText: text,
        messageId: tc.id,
      };
    });
  }

  const items: ScreenshotItem[] = [];
  for (let i = 0; i < tcs.length; i++) {
    const tc = tcs[i];
    const beforeSrc =
      i === 0 ? initialScreenshotUrl : tcs[i - 1].screenshotSignedUrl;
    // No before-frame for the very first action without an initial screenshot —
    // skip it so we never draw the dot on the wrong frame.
    if (!beforeSrc) continue;
    const { coordinate, action, text } = extractActionData(
      tc.inputData,
      tc.outputData as Record<string, unknown> | null,
    );
    items.push({
      id: tc.id,
      src: beforeSrc,
      label: getToolCallLabel(tc.toolType),
      coordinate,
      actionType: action,
      actionText: text,
      messageId: tc.id,
    });
  }

  // Final frame: the last action's result, shown without an overlay (no
  // messageId — it isn't an action step).
  const last = tcs[tcs.length - 1];
  items.push({
    id: `${last.id}-final`,
    src: last.screenshotSignedUrl,
    label: finalLabel,
  });

  return items;
}

/**
 * Trace actions identify persisted tool calls, while live frames carry A2A
 * message IDs. Honour the current selection using the collection that contains it;
 * otherwise retain the viewer's live-to-persisted handoff and injected data.
 */
export function selectScreenshotItems({
  externalItems,
  isComplete,
  liveItems,
  toolCallItems,
  selection,
}: {
  externalItems?: ScreenshotItem[];
  isComplete: boolean;
  liveItems: ScreenshotItem[];
  toolCallItems: ScreenshotItem[];
  selection?: ScreenshotSelection | null;
}): ScreenshotItem[] {
  if (externalItems) return externalItems;
  const focusesToolCall = toolCallItems.some((item) =>
    screenshotMatchesSelection(item, selection),
  );
  if (toolCallItems.length > 0 && (isComplete || focusesToolCall)) {
    return toolCallItems;
  }
  return liveItems.length > 0 ? liveItems : toolCallItems;
}

/** The tool-call fields the completed-run thread reads. */
type ThreadToolCall = Pick<
  ToolCallRunResponse,
  | 'id'
  | 'toolType'
  | 'inputData'
  | 'outputData'
  | 'screenshotSignedUrl'
  | 'startedAt'
>;

/**
 * Build the completed-run message thread from screenshot-bearing tool calls: one entry per step,
 * sharing the tool-call id with its screenshot (see buildScreenshotItems) so the gallery ↔ thread
 * linking stays structural. Action + reasoning + metadata all come from the durable audit row, so
 * this survives the live session. Shared by AgentSessionViewer (a full run's thread) and any
 * consumer that renders a filtered slice of a run's timeline (e.g. one SOP replay step's trace).
 */
export function buildToolCallMessages(
  toolCalls: ThreadToolCall[],
): SessionMessageEntry[] {
  return toolCalls
    .filter((tc) => Boolean(tc.screenshotSignedUrl))
    .map((tc) => {
      const { action } = extractActionData(
        tc.inputData,
        tc.outputData as Record<string, unknown> | null,
      );
      const out = tc.outputData as Record<string, unknown> | null;
      const reasoning = typeof out?.reasoning === 'string' ? out.reasoning : '';
      return {
        id: tc.id,
        messageId: tc.id,
        action: action ?? tc.toolType?.split('.').pop(),
        // inputData carries the action params (key/coordinate/scroll/…) so the
        // completed thread shows the same detail as the live one.
        metadata: tc.inputData,
        message: reasoning,
        level: 'info' as const,
        timestamp: tc.startedAt ? new Date(tc.startedAt).getTime() : 0,
      };
    });
}

/** What a run's persisted timeline offers the session viewer. */
export interface TimelineSessionMedia {
  /** Every row that captured a frame — the viewer's gallery for a finished run. */
  screenshotToolCalls: ToolCallRunResponse[];
  /**
   * The desktop session this run drove, recovered from its login row.
   *
   * A finished run has no live session to ask, so the id is read back out of the tool call that
   * opened it. Callers prefer a live session when there is one and fall back to this.
   */
  sessionId: string | null;
}

const REMOTE_DESKTOP_LOGIN_TOOL_TYPE =
  'agent.remote_desktop.login_to_remote_desktop';

/** Read the viewer's inputs out of a run timeline. Shared because three pages render the same
 * viewer from the same timeline and had each grown their own copy of this. */
export function timelineSessionMedia(
  timeline: { toolCalls: ToolCallRunResponse[] } | undefined | null,
): TimelineSessionMedia {
  if (!timeline) return { screenshotToolCalls: [], sessionId: null };

  let sessionId: string | null = null;
  for (const toolCall of timeline.toolCalls) {
    if (toolCall.toolType !== REMOTE_DESKTOP_LOGIN_TOOL_TYPE) continue;
    const found = toolCall.outputData?.sessionId;
    // The LAST login wins: a run that reconnected drove the newer session.
    if (typeof found === 'string') sessionId = found;
  }

  return {
    screenshotToolCalls: timeline.toolCalls.filter(
      (tc) => tc.screenshotSignedUrl,
    ),
    sessionId,
  };
}
